import {
  cloneElement,
  isValidElement,
  useCallback,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { Controller, type Control, type FieldError } from 'react-hook-form';
import styled from 'styled-components';
import {
  Form,
  Input,
  Radio,
  Dropdown as ThemeDropdown,
  type DropdownProps,
  type StrictRadioProps,
  type SemanticWIDTHS,
} from 'semantic-ui-react';
import { Calendar, CaretDown, Eye, EyeSlash, Info } from '@phosphor-icons/react';
import PhoneInput from 'react-phone-input-2';
import { HexColorPicker } from 'react-colorful';
import DatePicker, { type DatePickerProps } from 'react-datepicker';
import { DangerText, NormalSliderCheckbox, StyledCheckbox } from './Common';
import { CommonUtility } from '../utility/common';
import 'react-phone-input-2/lib/style.css';
import { NumberMaskInput } from './CustomMaskInput';
import { CountryDropdownStyle } from './Dropdown';
import { ColorSwatch, Picker, Popover, useClickOutside } from './ColorPicker';
import { DateFormat, theme } from '../utility';
import { DatepickerContainer } from './Datepicker';
import { DarkPopup } from '../components/Popup';
import type { IDropdownOption } from 'types/common/dropdown';

const CustomField = styled(Form.Field)`
  margin-bottom: 1em !important;
  .custom-label {
    margin-bottom: 0.2rem;
  }
  &.required {
    .custom-label {
      label:after {
        margin: -0.2em 0 0 0.2em;
        content: '*';
        color: #ff4218;
      }
      .error-message {
        font-size: 12px;
        float: right;
        color: #ff4218;
      }
    }
  }

  &.mb-0 {
    margin-bottom: 0 !important;
  }
  &.mb-2 {
    margin-bottom: 1rem !important;
  }

  &.success {
    .hint {
      color: ${({ theme }) => theme.colors.success};
    }
  }
  &.large-checkbox svg {
    height: 20px;
    left: 3px;
  }
  &.supplier-lead-time {
    min-width: 135px;
  }
  @media (max-width: 1340px) {
    &.supplier-country .ui.dropdown .text {
      width: 150px;
      width: 10em;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
`;

const MuteLabel = styled.label`
  color: ${({ theme }) => theme.colors.grey} !important;
  font-weight: normal !important;
`;

interface IInputContainerProps {
  inline?: boolean;
}

const InputContainer = styled.div<IInputContainerProps>`
  display: ${({ inline }) => (inline ? 'inline-flex' : 'flex')};
  align-items: center;
  position: relative;

  &.similar-height {
    input {
      height: 40px;
    }
  }

  &.input-height {
    input {
      height: 37px;
    }
  }

  &.align-unset {
    align-items: unset;
  }

  .success {
    border-color: #a3c293 !important;
    color: #2c662d !important;
    background-color: #fcfff5 !important;
  }

  .relative {
    position: relative;
  }

  textarea {
    line-height: 1 !important;
  }
`;

interface IControlledTextFormFieldProps {
  control: Control<any>;
  name: string;
  defaultValue?: string;
  required?: boolean;
  error?: any;
  inline?: boolean;
  inputRight?: ReactNode;
  label?: ReactNode;
  hint?: string | null;
  width?: SemanticWIDTHS;
  placeholder?: string;
  fieldClass?: string;
  inputContainerClass?: string;
  onChange?: (value: string) => void;
  helpText?: ReactNode;
  helpPosition?: string;
  hintTitleCase?: boolean;
  [key: string]: any;
}

export function ControlledTextFormField({
  control,
  name,
  defaultValue = '',
  required,
  error,
  inline,
  inputRight,
  label,
  hint,
  width,
  placeholder,
  fieldClass,
  inputContainerClass,
  onChange,
  helpText,
  helpPosition,
  hintTitleCase,
  ...rest
}: IControlledTextFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          inline={inline || false}
          className={fieldClass}
        >
          {label && (
            <MuteLabel className={helpText ? 'd-flex align-items-center' : ''}>
              {label}
            </MuteLabel>
          )}
          <InputContainer inline={inline} className={inputContainerClass}>
            <input
              type="text"
              data-field-value={name}
              placeholder={
                placeholder || (isValidElement(label) ? '' : (label as string))
              }
              onChange={e => {
                let trimmedValue = e.target.value;
                if (trimmedValue && trimmedValue.trimStart) {
                  trimmedValue = trimmedValue.trimStart();
                  trimmedValue = trimmedValue.replace(/ {2}$/g, ' ');
                }
                controlOnChange(trimmedValue);
                if (onChange) {
                  onChange(trimmedValue);
                }
              }}
              value={value}
              {...rest}
            />
            {inputRight}
          </InputContainer>
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

const PasswordIcon = styled.div`
  position: absolute;
  right: 7px;
  top: 0px;
  cursor: pointer;
  display: flex !important;
  align-items: center !important;
  height: 100% !important;
`;

interface IPasswordFormFieldProps {
  control: Control<any>;
  name: string;
  value?: string;
  required?: boolean;
  error?: any;
  inline?: boolean;
  inputRight?: ReactNode;
  label?: ReactNode;
  hint?: string;
  width?: SemanticWIDTHS;
  placeholder?: string;
  fieldClass?: string;
  defaultValue?: string;
  [key: string]: any;
}

export function PasswordFormField({
  control,
  name,
  value,
  required,
  error,
  inline,
  inputRight,
  label,
  hint,
  width,
  placeholder,
  fieldClass,
  defaultValue = '',
  ...rest
}: IPasswordFormFieldProps) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          inline={inline || false}
          className={fieldClass}
        >
          {label && <MuteLabel>{label}</MuteLabel>}
          <InputContainer inline={inline}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={
                placeholder || (isValidElement(label) ? '' : (label as string))
              }
              onChange={e => {
                controlOnChange(e.target.value);
              }}
              data-field-value={name}
              value={value}
              {...rest}
            />
            <PasswordIcon>
              {showPassword ? (
                <EyeSlash onClick={() => setShowPassword(!showPassword)} size={18} />
              ) : (
                <Eye onClick={() => setShowPassword(!showPassword)} size={18} />
              )}
            </PasswordIcon>
          </InputContainer>
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

const StyledPhoneInput = styled(PhoneInput)`
  .special-label {
    display: none;
  }
  .form-control {
    padding-left: 57px !important;
    height: 38px;
  }
  height: 38px;
  .country-list {
    width: 100%;
    bottom: 15px;
  }
  .flag-dropdown {
    position: relative;
    border: 0;
  }
  .selected-flag {
    margin-top: -20px;
  }
`;

interface IControlledPhoneFormFieldProps {
  control: Control<any>;
  name: string;
  defaultValue?: string;
  required?: boolean;
  error?: any;
  inline?: boolean;
  inputRight?: ReactNode;
  label?: ReactNode;
  hint?: string;
  width?: SemanticWIDTHS;
  fieldClass?: string;
  inputContainerClass?: string;
  onChange?: (phone: string) => void;
  helpText?: ReactNode;
  helpPosition?: string;
  setDialCodeLength?: (length: number) => void;
  countryCode?: string;
  [key: string]: any;
}

export function ControlledPhoneFormField({
  control,
  name,
  defaultValue = '',
  required,
  error,
  inline,
  inputRight,
  label,
  hint,
  width,
  fieldClass,
  inputContainerClass,
  onChange,
  helpText,
  helpPosition,
  setDialCodeLength,
  countryCode,
  ...rest
}: IControlledPhoneFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          inline={inline || false}
          className={fieldClass}
        >
          {label && (
            <MuteLabel className={helpText ? 'd-flex align-items-center' : ''}>
              {label}
            </MuteLabel>
          )}
          <InputContainer inline={inline} className={inputContainerClass}>
            <StyledPhoneInput
              country={countryCode || 'us'}
              data-field-value={name}
              value={value}
              onChange={(phone: string, data: any) => {
                controlOnChange(phone);
                if (setDialCodeLength) {
                  setDialCodeLength(data.dialCode.length);
                }
                if (onChange) {
                  onChange(phone);
                }
              }}
              enableSearch
              disableSearchIcon
              {...rest}
            />
            {inputRight}
          </InputContainer>
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

interface IStyledDropdownProps {
  imageMarginTop?: string;
}

export const StyledDropdown = styled(ThemeDropdown)<IStyledDropdownProps>`
  min-width: 5em !important;
  a {
    background: ${({ theme }) => theme.colors.box} !important;
    box-shadow: none !important;
    border-radius: 15px !important;
    padding: 7px 13px !important;
  }
  &.ui.selection.dropdown.active,
  .ui.selection.dropdown.visible {
    z-index: 20;
  }
  .visible.menu.transition {
    max-height: 40vh !important;
  }

  img.image {
    margin-top: ${props => props.imageMarginTop ?? 'auto !important'};
  }

  &.similar-height {
    height: 40px;
  }
`;

const DropdownIcon = styled(CaretDown)`
  cursor: pointer;
  position: absolute;
  width: auto;
  height: auto;
  line-height: 1.21428571em;
  top: 0.78571429em;
  right: 0.7em;
  z-index: 3;
  margin: -0.78571429em;
  padding: 0.7em;
  opacity: 0.8;
  transition: opacity 0.1s ease;
`;

interface ICustomDropdownProps extends DropdownProps {
  loading?: boolean;
}

function CustomDropdown({ loading, ...props }: ICustomDropdownProps) {
  return (
    <StyledDropdown
      loading={loading}
      icon={
        loading ? (
          <i className="dropdown icon" aria-hidden="true" />
        ) : (
          <DropdownIcon size={18} />
        )
      }
      {...props}
    />
  );
}

interface IDropdownFormFieldProps {
  required?: boolean;
  loading?: boolean;
  options: IDropdownOption[];
  error?: any;
  label?: ReactNode;
  hint?: string;
  control: Control<any>;
  name: string;
  placeholder?: string;
  defaultValue?: any;
  width?: SemanticWIDTHS;
  dropdownChanged?: (item: any) => void;
  inline?: boolean;
  fieldClass?: string;
  helpText?: ReactNode;
  sorted?: boolean;
  [key: string]: any;
}

export function DropdownFormField({
  required,
  loading,
  options: data,
  error,
  label,
  hint,
  control,
  name,
  placeholder,
  defaultValue = null,
  width,
  dropdownChanged,
  inline,
  fieldClass,
  helpText,
  ...rest
}: IDropdownFormFieldProps) {
  const options =
    rest?.sorted === false
      ? data
      : CommonUtility.sortDropdownOptions(data as { text: string }[]);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          className={fieldClass}
        >
          {label && (
            <MuteLabel className={helpText ? 'd-flex align-items-center' : ''}>
              {label}
            </MuteLabel>
          )}
          <CustomDropdown
            inline={inline}
            loading={loading}
            placeholder={placeholder || (isValidElement(label) ? '' : (label as string))}
            search
            searchInput={{ autoComplete: 'none' }}
            selectOnBlur={false}
            selection
            data-field-value={name}
            options={options}
            onChange={(e, item) => {
              onChange(item.value);
              if (dropdownChanged) {
                dropdownChanged(item);
              }
            }}
            value={value}
            {...rest}
          />
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

interface INumberFormFieldProps {
  defaultValue?: number | null;
  name: string;
  control: Control<any>;
  value?: number | null;
  required?: boolean;
  error?: any;
  label?: ReactNode;
  hint?: string;
  width?: SemanticWIDTHS;
  placeholder?: string;
  inputRight?: ReactNode;
  inputLeft?: ReactNode;
  maskOptions?: any;
  fieldClass?: string;
  inline?: boolean;
  helpText?: ReactNode;
  helpPosition?: string;
  labelDescription?: string;
  [key: string]: any;
}

export function NumberFormField({
  defaultValue = null,
  name,
  control,
  value,
  required,
  error,
  label,
  hint,
  width,
  placeholder,
  inputRight,
  inputLeft,
  maskOptions,
  fieldClass,
  inline,
  helpText,
  helpPosition,
  labelDescription,
  ...rest
}: INumberFormFieldProps) {
  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { onChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          className={fieldClass}
          inline={inline || false}
        >
          {label && (
            <MuteLabel className={helpText ? 'd-flex align-items-center' : ''}>
              {label}
            </MuteLabel>
          )}
          <InputContainer inline={inline} {...rest}>
            {inputLeft}
            <NumberMaskInput
              value={value}
              data-field-value={name}
              maskOptions={maskOptions}
              placeholder={
                placeholder || (isValidElement(label) ? '' : (label as string))
              }
              {...rest}
              onChange={newValue => onChange(newValue)}
            />
            {inputRight}
          </InputContainer>
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

interface ICountryDropdownFormFieldProps {
  required?: boolean;
  loading?: boolean;
  options: IDropdownOption[];
  error?: any;
  label?: ReactNode;
  hint?: string;
  control: Control<any>;
  name: string;
  className?: string;
  placeholder?: string;
  defaultValue?: any;
  width?: SemanticWIDTHS;
  dropdownChanged?: (item: any) => void;
  fieldClass?: string;
  labelError?: FieldError;
  [key: string]: any;
}

export function CountryDropdownFormField({
  required,
  loading,
  options,
  error,
  label,
  hint,
  control,
  name,
  className,
  placeholder,
  defaultValue,
  width,
  dropdownChanged,
  fieldClass,
  labelError,
  ...rest
}: ICountryDropdownFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value, onBlur } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          className={fieldClass}
        >
          <div className="custom-label">
            {label && <MuteLabel>{label}</MuteLabel>}
            {labelError && <span className="error-message">{labelError?.message}</span>}
          </div>
          <CountryDropdownStyle
            loading={loading}
            placeholder={placeholder || (isValidElement(label) ? '' : (label as string))}
            search
            data-field-value={name}
            searchInput={{ autoComplete: 'none' }}
            selectOnBlur={false}
            selection
            options={options}
            onChange={(e, item) => {
              onChange(item.value);
              if (dropdownChanged) {
                dropdownChanged(item);
              }
            }}
            value={value}
            onBlur={onBlur}
            className={`mute-dropdown ${className}`}
            {...rest}
          />
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

interface IControlledTextAreaFormFieldProps {
  required?: boolean;
  control: Control<any>;
  defaultValue?: string;
  name: string;
  error?: any;
  label?: ReactNode;
  hint?: string;
  width?: SemanticWIDTHS;
  placeholder?: string;
  inputRight?: ReactNode;
  fieldClass?: string;
  [key: string]: any;
}

export function ControlledTextAreaFormField({
  required,
  control,
  defaultValue = '',
  name,
  error,
  label,
  hint,
  width,
  placeholder,
  inputRight,
  fieldClass,
  ...rest
}: IControlledTextAreaFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          className={fieldClass}
        >
          <MuteLabel>{label}</MuteLabel>
          <InputContainer>
            <textarea
              placeholder={
                placeholder || (isValidElement(label) ? '' : (label as string))
              }
              autoComplete="none"
              onChange={e => {
                controlOnChange(e.target.value);
              }}
              data-field-value={name}
              value={value}
              {...rest}
            />
            {inputRight}
          </InputContainer>
          {hint && <DangerText className="hint">{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

interface ICheckboxFormFieldProps {
  required?: boolean;
  error?: any;
  label?: ReactNode;
  text?: ReactNode;
  disabled?: boolean;
  hint?: string;
  control: Control<any>;
  name: string;
  width?: SemanticWIDTHS;
  defaultValue?: boolean;
  fieldProps?: any;
  fieldClass?: string;
  helpText?: ReactNode;
  [key: string]: any;
}

export function CheckboxFormField({
  required,
  error,
  label,
  text,
  disabled,
  hint,
  control,
  name,
  width,
  defaultValue = false,
  fieldProps,
  fieldClass,
  helpText,
  ...rest
}: ICheckboxFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          className={fieldClass}
          {...fieldProps}
        >
          <div className="custom-label">{label && <MuteLabel>{label}</MuteLabel>}</div>
          <StyledCheckbox
            className={`mr-2 ${fieldClass}`}
            name={name}
            data-field-value={name}
            checked={value}
            label={text}
            disabled={disabled}
            onChange={(_, { checked }) => onChange(checked)}
            {...rest}
          />
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

interface ISliderFormFieldProps {
  required?: boolean;
  error?: any;
  label?: ReactNode;
  text?: ReactNode;
  disabled?: boolean;
  hint?: string;
  control: Control<any>;
  name: string;
  width?: SemanticWIDTHS;
  defaultValue?: boolean;
  fieldProps?: any;
  fieldClass?: string;
  helpText?: ReactNode;
  [key: string]: any;
}

export function SliderFormField({
  required,
  error,
  label,
  text,
  disabled,
  hint,
  control,
  name,
  width,
  defaultValue = false,
  fieldProps,
  fieldClass,
  helpText,
  ...rest
}: ISliderFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          className={fieldClass}
          {...fieldProps}
        >
          <div className="custom-label">{label && <MuteLabel>{label}</MuteLabel>}</div>
          <NormalSliderCheckbox
            className={`mr-2 ${fieldClass}`}
            name={name}
            data-field-value={name}
            checked={value}
            label={text}
            disabled={disabled}
            onChange={(_, { checked }) => onChange(checked)}
            {...rest}
          />
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

interface IControlledColorPickerFieldProps {
  control: Control<any>;
  name: string;
  defaultValue?: string;
  required?: boolean;
  error?: any;
  inline?: boolean;
  inputRight?: ReactNode;
  label?: ReactNode;
  hint?: string;
  width?: SemanticWIDTHS;
  fieldClass?: string;
  inputContainerClass?: string;
  onChange?: (color: string) => void;
  helpText?: ReactNode;
  helpPosition?: string;
  hintTitleCase?: boolean;
  [key: string]: any;
}

export function ControlledColorPickerField({
  control,
  name,
  defaultValue = '',
  required,
  error,
  inline,
  inputRight,
  label,
  hint,
  width,
  fieldClass,
  inputContainerClass,
  onChange,
  helpText,
  helpPosition,
  hintTitleCase,
  ...rest
}: IControlledColorPickerFieldProps) {
  const popover = useRef<HTMLDivElement>(null);
  const [isOpen, toggle] = useState(false);

  const close = useCallback(() => toggle(false), []);
  useClickOutside(popover, close);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange: controlOnChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          inline={inline || false}
          className={fieldClass}
        >
          {label && (
            <MuteLabel className={helpText ? 'd-flex align-items-center' : ''}>
              {label}
            </MuteLabel>
          )}
          <InputContainer inline={inline} className={inputContainerClass}>
            <Picker>
              <ColorSwatch backgroundColor={value} onClick={() => toggle(true)} />

              {isOpen && (
                <Popover ref={popover}>
                  <HexColorPicker
                    data-field-value={name}
                    onChange={(color: string) => {
                      controlOnChange(color);
                      if (onChange) {
                        onChange(color);
                      }
                    }}
                    color={value}
                    {...rest}
                  />
                </Popover>
              )}
            </Picker>
            {inputRight}
          </InputContainer>
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

const CalenderInput = styled(Input)`
  .icon {
    cursor: default;
    position: absolute;
    line-height: 1;
    text-align: center;
    top: 6px;
    right: 5px;
    opacity: 0.5;
    margin: 0;
    border-radius: 0 0.28571429rem 0.28571429rem 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  &:focus-within .icon {
    opacity: 0.9 !important;
  }
`;

type IDateFormFieldProps = DatePickerProps & {
  required?: boolean;
  error?: any;
  label?: ReactNode;
  hint?: string;
  control: Control<any>;
  name: string;
  width?: SemanticWIDTHS;
  defaultValue?: Date | string;
  inline?: boolean;
  helpText?: ReactNode;
  fieldClass?: string;
  placeholder?: string;
  icon?: ReactElement | null;
};

export function DateFormField({
  required,
  error,
  label,
  hint,
  control,
  name,
  width,
  defaultValue = '',
  inline,
  helpText,
  fieldClass = '',
  placeholder,
  icon = null,
  ...rest
}: IDateFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField
          className={fieldClass}
          required={required}
          error={!!error}
          width={width}
          inline={inline || false}
        >
          {label && (
            <MuteLabel className={helpText ? 'd-flex align-items-center' : ''}>
              {label}
            </MuteLabel>
          )}
          <DatepickerContainer>
            <DatePicker
              formatWeekDay={nameOfDay => nameOfDay.substring(0, 3)}
              selected={value as any}
              onChange={(date: any) => {
                onChange(date);
              }}
              data-field-value={name}
              placeholderText={
                placeholder || (isValidElement(label) ? '' : (label as string))
              }
              customInput={
                <CalenderInput icon={<Calendar className="icon" size={26} />} />
              }
              dateFormat={DateFormat.datepicker}
              disabledKeyboardNavigation
              {...rest}
            />
          </DatepickerContainer>
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}

const RadioInfoIcon = styled(Info)`
  margin-bottom: -3px;
  margin-left: -5px;
  margin-right: 10px;
  cursor: pointer;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 7px;
  row-gap: 7px;
`;

interface IRadioOption {
  value: any;
  text: ReactNode;
  disabled?: boolean;
  className?: string;
  icon?: ReactElement;
  helpText?: ReactNode;
}

interface IRadioFormFieldProps extends Partial<StrictRadioProps> {
  required?: boolean;
  options: IRadioOption[];
  error?: any;
  label?: ReactNode;
  hint?: string;
  control: Control<any>;
  name: string;
  defaultValue?: any;
  width?: SemanticWIDTHS;
  helpText?: ReactNode;
  fieldClass?: string;
}

export function RadioFormField({
  required,
  options,
  error,
  label,
  hint,
  control,
  name,
  defaultValue,
  width,
  helpText,
  fieldClass,
  ...rest
}: IRadioFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField
          required={required}
          error={!!error}
          width={width}
          className={fieldClass}
        >
          {label && (
            <MuteLabel className={helpText ? 'd-flex align-items-center' : ''}>
              {label}
            </MuteLabel>
          )}
          <OptionsContainer>
            {options.map(item => (
              <div
                key={item.value}
                className={`ui radio checkbox d-flex radio-container align-items-center ${
                  item.className || ''
                }`}
              >
                <Radio
                  className="mr-2"
                  name={name}
                  data-field-value={`${name}-${item.value}`}
                  checked={value === item.value}
                  value={item.value}
                  label={item.text}
                  disabled={item.disabled}
                  onChange={(_, { value }) => onChange(value)}
                  {...rest}
                />
                {isValidElement(item.icon) && cloneElement(item.icon)}
                {item.helpText && (
                  <DarkPopup
                    trigger={<RadioInfoIcon color={theme.colors.grey} />}
                    position="top center"
                  >
                    {item.helpText}
                  </DarkPopup>
                )}
              </div>
            ))}
          </OptionsContainer>
          {hint && <DangerText>{hint}</DangerText>}
        </CustomField>
      )}
    />
  );
}
interface IMaskNumberUncontrolledFormFieldProps {
  name: string;
  value: string | number;
  setValue: (value: string | number) => void;
  required?: boolean;
  error?: boolean | string;
  label?: ReactNode;
  hint?: string;
  width?: SemanticWIDTHS;
  placeholder?: string;
  inputRight?: ReactNode;
  inputLeft?: ReactNode;
  maskOptions?: Record<string, any>;
  fieldClass?: string;
  inline?: boolean;
  helpText?: string;
  helpPosition?: string;
  labelDescription?: string;
  style?: CSSProperties;
  [key: string]: any;
}

export function MaskNumberUncontrolledFormField({
  name,
  value,
  setValue,
  required,
  error,
  label,
  hint,
  width,
  placeholder,
  inputRight,
  inputLeft,
  maskOptions,
  fieldClass,
  inline,
  helpText,
  helpPosition,
  labelDescription,
  ...rest
}: IMaskNumberUncontrolledFormFieldProps) {
  return (
    <CustomField
      required={required}
      error={!!error}
      width={width}
      className={fieldClass}
      inline={inline || false}
    >
      {label && (
        <MuteLabel
          className={[
            helpText ? 'd-flex align-items-center' : '',
            labelDescription ? 'has-description' : '',
          ].join(' ')}
        >
          {label}
        </MuteLabel>
      )}
      <InputContainer inline={inline} {...rest}>
        {inputLeft && inputLeft}
        <NumberMaskInput
          style={inputLeft ? { paddingLeft: 20 } : {}}
          value={value}
          data-field-value={name}
          onChange={(newValue: string | number) => setValue(newValue)}
          maskOptions={maskOptions}
          placeholder={placeholder || (isValidElement(label) ? '' : (label as string))}
          {...rest}
        />
        {inputRight && inputRight}
      </InputContainer>
      {hint && <DangerText>{hint}</DangerText>}
    </CustomField>
  );
}

interface IMaskedNumberFormFieldProps {
  defaultValue?: string | number | null;
  name: string;
  control: Control<any>;
  inputLeft?: ReactNode;
  [key: string]: any;
}

export function MaskedNumberFormField({
  defaultValue = null,
  name,
  control,
  inputLeft,
  ...rest
}: IMaskedNumberFormFieldProps) {
  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { onChange, value } }) => (
        <MaskNumberUncontrolledFormField
          inputLeft={inputLeft}
          value={value}
          name={name}
          setValue={(val: string | number) => onChange(val)}
          {...rest}
        />
      )}
    />
  );
}
